/**
 * Cookie / analytics consent helpers.
 *
 * State model:
 *   - localStorage.cookieConsent === "accepted"  → persistent acceptance
 *       (tracking continues across sessions, banner hidden)
 *   - sessionStorage.cookieConsent === "denied" → session-only denial
 *       (no tracking for this tab/session, banner hidden until next session)
 *   - neither set → the banner is shown. Tracking is allowed by default
 *       (per product spec: "accept" is the default).
 *
 * A "open-cookie-consent" CustomEvent is dispatched on `window` to re-open
 * the banner on demand from any footer link.
 */

const LS_KEY = "cookieConsent";
const SS_KEY = "cookieConsent";
export const OPEN_CONSENT_EVENT = "open-cookie-consent";

const safeLocal = () => {
  try { return typeof window !== "undefined" ? window.localStorage : null; } catch { return null; }
};
const safeSession = () => {
  try { return typeof window !== "undefined" ? window.sessionStorage : null; } catch { return null; }
};

export function getConsent() {
  const ls = safeLocal();
  if (ls && ls.getItem(LS_KEY) === "accepted") return "accepted";
  const ss = safeSession();
  if (ss && ss.getItem(SS_KEY) === "denied") return "denied";
  return "unknown";
}

/** Whether the visitor should be tracked right now. */
export function trackingAllowed() {
  // Default is to allow ("accept is the default") — only explicit denial blocks.
  return getConsent() !== "denied";
}

/** Whether the consent banner should be shown on page load. */
export function shouldShowBanner() {
  return getConsent() === "unknown";
}

export function acceptConsent() {
  const ls = safeLocal();
  if (ls) {
    try { ls.setItem(LS_KEY, "accepted"); } catch { /* quota / private mode */ }
  }
  // Make sure a previous session denial doesn't override the new acceptance.
  const ss = safeSession();
  if (ss) {
    try { ss.removeItem(SS_KEY); } catch { /* ignore */ }
  }
}

export function denyConsent() {
  // Session-only: do NOT persist in localStorage.
  const ss = safeSession();
  if (ss) {
    try { ss.setItem(SS_KEY, "denied"); } catch { /* ignore */ }
  }
  // Also clear any prior persistent acceptance so the deny takes effect.
  const ls = safeLocal();
  if (ls) {
    try { ls.removeItem(LS_KEY); } catch { /* ignore */ }
  }
}

/** Reset to "unknown" so the banner reopens (used by the footer link). */
export function resetConsent() {
  const ls = safeLocal();
  const ss = safeSession();
  try { ls && ls.removeItem(LS_KEY); } catch { /* ignore */ }
  try { ss && ss.removeItem(SS_KEY); } catch { /* ignore */ }
}

export function openConsentBanner() {
  if (typeof window === "undefined") return;
  // Re-open even if a prior choice was made; the banner clears state on mount.
  window.dispatchEvent(new CustomEvent(OPEN_CONSENT_EVENT));
}
